import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dashboardMockData from '../../data/dashboardMockData';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const defaultTimeframe = 'today';

const emptyDashboardData = {
  summary: {
    totalTransactions: 0,
    totalTickets: 0
  },
  hourly: [],
  origin: [],
  destination: [],
  partners: []
};

const initialState = {
  timeframe: defaultTimeframe,
  data: emptyDashboardData,
  loading: false,
  error: null,
  lastUpdated: null
};

const buildAuthHeaders = (getState, extra = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra
  };

  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }

  return headers;
};

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  let data;

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

const normalizeSummary = (summary = {}) => ({
  totalTransactions: Number(summary.total_transactions ?? summary.totalTransactions ?? 0),
  totalTickets: Number(summary.total_tickets ?? summary.totalTickets ?? 0)
});

const normalizeBarItems = (items = []) =>
  Array.isArray(items)
    ? items.map((item) => ({
        label: String(item.label ?? item.hour ?? item.station_code ?? item.partner_name ?? item.name ?? '-'),
        value: Number(item.value ?? item.total_tickets ?? item.total ?? 0)
      }))
    : [];

const normalizeHourlyItems = (items = []) =>
  Array.isArray(items)
    ? items.map((item) => ({
        label: String(item.label ?? item.hour ?? '-'),
        transactions: Number(item.transactions ?? item.total_transactions ?? 0),
        tickets: Number(item.tickets ?? item.total_tickets ?? 0)
      }))
    : [];

const normalizePartnerItems = (items = []) => {
  const fallbackColors = ['#0c53b7', '#00a76f', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

  return Array.isArray(items)
    ? items.map((item, index) => ({
        label: String(item.label ?? item.partner_name ?? item.name ?? '-'),
        value: Number(item.value ?? item.total_transactions ?? item.total ?? 0),
        color: item.color || fallbackColors[index % fallbackColors.length]
      }))
    : [];
};

const normalizeDashboardPayload = (payload = {}) => ({
  summary: normalizeSummary(payload.summary),
  hourly: normalizeHourlyItems(payload.hourly),
  origin: normalizeBarItems(payload.origin),
  destination: normalizeBarItems(payload.destination),
  partners: normalizePartnerItems(payload.partners)
});

export const fetchDashboardOverview = createAsyncThunk(
  'dashboard/fetchOverview',
  async (timeframe = defaultTimeframe, { getState, dispatch, rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams({ timeframe });
      const response = await fetch(`${API_BASE_URL}/api/dashboard/overview?${searchParams.toString()}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const payload = data?.data || data?.resultData || data;

      return {
        timeframe,
        data: normalizeDashboardPayload(payload),
        lastUpdated: payload?.last_updated || payload?.lastUpdated || null
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load dashboard overview.');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setDashboardTimeframe: (state, action) => {
      state.timeframe = action.payload || defaultTimeframe;
    },
    loadDashboardMockData: (state, action) => {
      const timeframe = action.payload || defaultTimeframe;
      state.timeframe = timeframe;
      state.data = dashboardMockData[timeframe] || dashboardMockData[defaultTimeframe];
      state.error = null;
      state.loading = false;
      state.lastUpdated = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardOverview.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.timeframe = action.meta.arg || defaultTimeframe;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.timeframe = action.payload.timeframe;
        state.data = action.payload.data;
        state.lastUpdated = action.payload.lastUpdated;
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load dashboard overview.';
      });
  }
});

export const { setDashboardTimeframe, loadDashboardMockData } = dashboardSlice.actions;

export default dashboardSlice.reducer;
